import React from 'react';
import { noop } from 'lodash';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';

import appConfig from '../appConfig';
import { getJwt } from '../utils/jwt';
import PortalSessionContext from './PortalSessionContext/PortalSessionContext';
import { SocketProvider } from './SocketContext';
import { getLinkGroups } from '../api/linkGroups';
import { UserStatus } from '../constants/user';
import useLocalStorage from '../hooks/useLocalstorage';
import { HIDE_BALANCE_KEY } from '../constants/localstorageKeys';

interface GlobalStore {
	notifications: Array<ToastNotification>;
	linkGroups: Array<PortalLinkGroup>;
	serverTimestamp: string | null | undefined;
	serverTimezone: string | null | undefined;
	updateLinkGroups: (data: Array<PortalLinkGroup>) => void;
	updateNotifications: React.Dispatch<React.SetStateAction<Array<ToastNotification>>>;
	hideBalance: boolean;
	setHideBalance: (hideBalance: boolean) => void;
	setWhatsNewShownVersion: (string) => void;
	whatsNewShownVersion: string | null;
}

const initialState: GlobalStore = {
	notifications: [],
	updateNotifications: noop,
	serverTimestamp: undefined,
	serverTimezone: undefined,
	linkGroups: [],
	hideBalance: false,
	updateLinkGroups: noop,
	setHideBalance: noop,
	setWhatsNewShownVersion: noop,
	whatsNewShownVersion: null,
};

const GlobalContext = React.createContext(initialState);

export const GlobalContextProvider: React.FC = ({ children }) => {
	const { user } = React.useContext(PortalSessionContext);
	const history = useHistory();

	const [whatsNewShownVersion, setWhatsNewShownVersion] = React.useState<string | null>(
		getCurrentWhatsNewShownVersion()
	);
	const [notifications, updateNotifications] = React.useState<ToastNotification[]>([]);
	const [linkGroups, updateLinkGroups] = React.useState<Array<PortalLinkGroup>>([]);
	const [serverTimestamp, setServerTimestamp] = React.useState<string | null | undefined>();
	const [serverTimezone, setServerTimezone] = React.useState<string | null | undefined>();
	const [hideBalance, setHideBalance] = useLocalStorage(HIDE_BALANCE_KEY);

	React.useEffect(() => {
		// skip if the account was created less than 30 seconds ago
		if (dayjs(user?.created_at).isAfter(dayjs().subtract(30, 'second'))) return;

		if (user?.status?.id === UserStatus.Locked) {
			updateNotifications((currentNotifications) => [
				...currentNotifications,
				{
					id: new Date().getTime(),
					title: 'Account Locked',
					message: 'Your account is locked, please verify your account before proceeding any further.',
					type: 'warning',
					link: {
						name: 'Verify',
						url: '/account/profile',
					},
				},
			]);
		}
	}, [user?.status?.id, user?.created_at]);

	// update linkgroups when the user logs in/out as links can be visible only to logged in users
	React.useEffect(() => {
		getLinkGroups()
			.then((response) => {
				updateLinkGroups(response.linkGroups);
				setServerTimestamp(response.serverTime);
				setServerTimezone(response.serverTimezone);
			})
			.catch(() => {
				// to avoid unhandled promise exception warning
			});
	}, [user?.id]);

	// show whatsnew screen when the user logs in and if it hasn't been shown
	React.useEffect(() => {
		if (!user?.id || !appConfig.portal.whatsNewSlidesVersion) return;

		if (whatsNewShownVersion !== appConfig.portal.whatsNewSlidesVersion) {
			setTimeout(() => {
				history.push('?modal=whatsnew');
			}, 2000);
		}
	}, [user?.id, history, whatsNewShownVersion]);

	React.useEffect(() => {
		storeWhatsNewShownVersion(whatsNewShownVersion);
	}, [whatsNewShownVersion]);

	return (
		<GlobalContext.Provider
			value={{
				notifications,
				hideBalance,
				linkGroups,
				setHideBalance,
				serverTimestamp,
				serverTimezone,
				updateLinkGroups,
				updateNotifications,
				setWhatsNewShownVersion,
				whatsNewShownVersion,
			}}
		>
			<SocketProvider
				isSignedIn={!!user}
				name="Portal"
				pusherKey={appConfig.portal.pusherKey ?? ''}
				pusherCluster={appConfig.portal.pusherCluster ?? ''}
				pusherHost={appConfig.portal.pusherHost ?? ''}
				pusherAuthEndpoint={`${appConfig.portal.apiUrl}/broadcasting/auth`}
				token={getJwt()}
			>
				{children}
			</SocketProvider>
		</GlobalContext.Provider>
	);
};

export default GlobalContext;

const whatsNewShownVersionKey = 'whatsNewShownVersion';

const getCurrentWhatsNewShownVersion = () => {
	return window.localStorage.getItem(whatsNewShownVersionKey);
};

const storeWhatsNewShownVersion = (value?: string | null) => {
	if (!value) window.localStorage.removeItem(whatsNewShownVersionKey);
	else window.localStorage.setItem(whatsNewShownVersionKey, value);
};
